import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getDashboardStats } from '../api/client';
import HeatmapViewer from '../components/heatmaps/HeatmapViewer';
import ScrollMap from '../components/heatmaps/ScrollMap';
import clsx from 'clsx';
import { Eye, MousePointerClick, ArrowDownUp, Move, Angry, Search } from 'lucide-react';

const HEATMAP_TYPES = [
  { value: 'click', label: 'Click', icon: MousePointerClick },
  { value: 'scroll', label: 'Scroll', icon: ArrowDownUp },
  { value: 'move', label: 'Move', icon: Move },
  { value: 'rage', label: 'Rage Click', icon: Angry },
] as const;

const DATE_RANGES = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

const DEVICE_TYPES = [
  { value: 'all', label: 'All Devices' },
  { value: 'desktop', label: 'Desktop' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'tablet', label: 'Tablet' },
];

export default function HeatmapsPage() {
  const [selectedUrl, setSelectedUrl] = useState('');
  const [type, setType] = useState<'click' | 'scroll' | 'move' | 'rage'>('click');
  const [device, setDevice] = useState('all');
  const [dateRange, setDateRange] = useState('30d');
  const [searchFilter, setSearchFilter] = useState('');

  // Fetch top pages for the page list
  const { data: stats, isLoading: pagesLoading } = useQuery({
    queryKey: ['dashboard-stats-for-heatmaps', dateRange],
    queryFn: () => getDashboardStats({ dateRange }),
  });

  const pages = (stats?.topPages || []).map((p) => ({
    url: p.urlPath || p.url || '/',
    views: p.views || 0,
  }));

  const filteredPages = searchFilter
    ? pages.filter((p) => p.url.toLowerCase().includes(searchFilter.toLowerCase()))
    : pages;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Heatmaps</h1>

      <div className="flex gap-6">
        {/* Page List Sidebar */}
        <div className="w-72 shrink-0">
          <div className="card p-0">
            <div className="border-b border-gray-100 p-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  placeholder="Filter pages..."
                  className="w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-3 text-sm text-gray-700 placeholder-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>
            </div>

            <div className="max-h-[600px] overflow-y-auto">
              {pagesLoading ? (
                <div className="flex h-32 items-center justify-center">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-200 border-t-brand-600" />
                </div>
              ) : filteredPages.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-400">
                  No pages found
                </div>
              ) : (
                filteredPages.map((page) => (
                  <button
                    key={page.url}
                    onClick={() => setSelectedUrl(page.url)}
                    className={clsx(
                      'flex w-full items-center justify-between border-b border-gray-50 px-3 py-2.5 text-left transition-colors last:border-0',
                      selectedUrl === page.url
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-gray-700 hover:bg-gray-50',
                    )}
                  >
                    <span className="mr-2 min-w-0 truncate text-sm font-medium">
                      {page.url}
                    </span>
                    <span className="flex shrink-0 items-center gap-1 text-xs text-gray-400">
                      <Eye className="h-3 w-3" />
                      {page.views.toLocaleString()}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="min-w-0 flex-1 space-y-4">
          {/* Controls */}
          <div className="card">
            <div className="flex flex-wrap items-center gap-4">
              {/* Type Tabs */}
              <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
                {HEATMAP_TYPES.map((ht) => {
                  const Icon = ht.icon;
                  return (
                    <button
                      key={ht.value}
                      onClick={() => setType(ht.value)}
                      className={clsx(
                        'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                        type === ht.value
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700',
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {ht.label}
                    </button>
                  );
                })}
              </div>

              {/* Device Filter */}
              <select
                value={device}
                onChange={(e) => setDevice(e.target.value)}
                className="rounded-lg border-gray-300 text-sm"
              >
                {DEVICE_TYPES.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>

              {/* Date Range */}
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="rounded-lg border-gray-300 text-sm"
              >
                {DATE_RANGES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>

              {selectedUrl && (
                <div className="ml-auto rounded-md bg-brand-50 px-3 py-1 text-sm font-medium text-brand-700">
                  {selectedUrl}
                </div>
              )}
            </div>
          </div>

          {/* Heatmap Viewer */}
          {selectedUrl ? (
            type === 'scroll' ? (
              <ScrollMap url={selectedUrl} device={device} dateRange={dateRange} />
            ) : (
              <HeatmapViewer url={selectedUrl} type={type} device={device} dateRange={dateRange} />
            )
          ) : (
            <div className="card flex h-96 items-center justify-center">
              <div className="text-center">
                <MousePointerClick className="mx-auto h-12 w-12 text-gray-300" />
                <p className="mt-3 text-lg font-medium text-gray-500">
                  Select a page to view heatmaps
                </p>
                <p className="mt-1 text-sm text-gray-400">
                  Choose a page from the list on the left to see click, scroll, or movement data
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
